/**
 * Confirmar antes de un movimiento con consecuencias (ADR-0018). La API solo avisa (no bloquea, para no trabar
 * scripts ni tokens); en la web se pide confirmación con el detalle de lo pendiente, a pedido del equipo:
 *
 * - **Cerrar una madre** (Hecho o Descartada) con subtareas abiertas: es fácil cerrarla sin darse cuenta de que
 *   quedan partes por hacer.
 * - **Avanzar una tarea bloqueada** (a un estado de `ESTADOS_QUE_AVISAN_BLOQUEO`: En curso, En revisión o Hecho)
 *   mientras alguna tarea que espera siga abierta: puede que haya que rehacer trabajo.
 * - **Cerrar una tarea que otras esperan** (Hecho o Descartada, con tareas abiertas en `bloqueaA`): al cerrarla,
 *   esas dejan de estar bloqueadas por ella; cerrarla sin querer las libera antes de tiempo.
 *
 * Si pasan varias cosas, es UN solo diálogo con todas las listas.
 *
 * Vale para todos los caminos que mueven una tarea: la píldora de estado de la ficha y el tablero (arrastrar o
 * los botones ← →). Si no hay nada pendiente, la acción va directa.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  avisaSiBloqueada,
  bloqueantesAbiertos,
  esEstadoCerrado,
  estaBloqueada,
  formatearNumero,
  progresoSubtareas,
  type Estado,
  type ProgresoSubtareas,
  type TareaRelacionada,
} from '@cdtec-backlog/contrato';
import { DialogoConfirmar } from '../../componentes/DialogoConfirmar';
import { useMostrarTrasRetardo } from '../../componentes/useMostrarTrasRetardo';
import { useSubtareas, useTareaDetalle } from './hooks';
import { ListaRelacionadas } from './ListaRelacionadas';

/** Lo que hace falta saber de la tarea que se mueve (sirve la tarea de la lista o el detalle). */
export interface TareaAMover {
  id: number;
  numero: number;
  estado: Estado;
  progresoSubtareas: ProgresoSubtareas | null;
  /** Sus bloqueantes (abiertos y cerrados), como los trae la lista o el detalle. */
  bloqueadaPor: TareaRelacionada[];
}

/** Subtareas abiertas según el avance: las descartadas no cuentan en `total`, así que abiertas = total − hechas. */
export function subtareasAbiertas(tarea: { progresoSubtareas: ProgresoSubtareas | null }): number {
  const progreso = tarea.progresoSubtareas;
  return progreso ? Math.max(0, progreso.total - progreso.hechas) : 0;
}

/** ¿Pasar de un estado abierto a uno cerrado dejando subtareas abiertas? */
export function pideConfirmarCierre(tarea: TareaAMover, estado: Estado): boolean {
  return esEstadoCerrado(estado) && !esEstadoCerrado(tarea.estado) && subtareasAbiertas(tarea) > 0;
}

/** ¿Llevar una tarea bloqueada (con bloqueantes abiertos) a En curso, En revisión o Hecho? */
export function pideConfirmarBloqueo(tarea: TareaAMover, estado: Estado): boolean {
  return estado !== tarea.estado && avisaSiBloqueada(estado) && estaBloqueada(tarea.bloqueadaPor);
}

/**
 * ¿Pasar de abierta a cerrada? Entonces hay que revisar si otras la esperan. Quiénes la esperan (`bloqueaA`) solo
 * viene en el detalle, así que esto no decide: el diálogo pide el detalle y solo pregunta si alguna sigue abierta.
 */
export function pideRevisarQuienesEsperan(tarea: TareaAMover, estado: Estado): boolean {
  return esEstadoCerrado(estado) && !esEstadoCerrado(tarea.estado);
}

/** Une IDs como se lee en español: «CDT-1», «CDT-1 y CDT-2», «CDT-1, CDT-2 y CDT-3». */
export function unirIds(ids: string[]): string {
  if (ids.length <= 1) return ids.join('');
  return `${ids.slice(0, -1).join(', ')} y ${ids[ids.length - 1]}`;
}

/** Consecuencia de cerrar una tarea que otras esperan: «Al cerrarla, CDT-104 y CDT-105 dejan de estar…». */
export function consecuenciaQuienesEsperan(
  esperan: readonly { numero: number }[],
  estado: Estado,
): string {
  const ids = unirIds(esperan.map((t) => formatearNumero(t.numero)));
  const verbo = esperan.length === 1 ? 'deja de estar bloqueada' : 'dejan de estar bloqueadas';
  return estado === 'DESCARTADA'
    ? `Al descartarla, ${ids} ${verbo} por ella aunque el trabajo no se haya hecho.`
    : `Al cerrarla, ${ids} ${verbo} por ella.`;
}

interface PropsDialogo {
  tarea: TareaAMover;
  estado: Estado;
  /** Revisar subtareas abiertas (cierre de una madre). */
  porSubtareas: boolean;
  /** Revisar bloqueantes abiertos. */
  porBloqueo: boolean;
  /** Revisar si otras tareas abiertas la esperan (se cierra; lo decide el detalle). */
  porQuienesEsperan: boolean;
  /** `conDialogo`: `true` si la persona confirmó en el diálogo; `false` si se siguió sin mostrarlo. */
  onConfirmar: (conDialogo: boolean) => void;
  onCancelar: () => void;
}

const BLOQUEO_NO_SE_QUITA =
  'El bloqueo no se quita: seguirá marcada como bloqueada hasta que esas tareas se cierren.';

const BLOQUEOS_EN_HISTORIAL = 'Los bloqueos quedan registrados en el historial.';

/**
 * ¿Ya hay datos pedidos DESPUÉS de abrir el diálogo? Un detalle o una lista en caché pueden ser viejos (p. ej. el
 * detalle de hace un rato dice «sin bloqueos» y la lista de ahora dice «bloqueada»): mientras se vuelven a pedir,
 * se cuenta como cargando. Si estaban frescos (dentro de `staleTime`), no se piden y valen al tiro.
 */
function sinDatosFrescos(consulta: {
  isPending: boolean;
  isFetching: boolean;
  isFetchedAfterMount: boolean;
}): boolean {
  return consulta.isPending || (consulta.isFetching && !consulta.isFetchedAfterMount);
}

/**
 * Título y botón del diálogo según a qué estado se mueve (no cambian si los datos frescos dicen otra cosa). Al
 * descartar una madre se mantiene «¿Cerrar … como Descartada?»; si no es madre, el diálogo solo puede ser por quienes
 * la esperan: «¿Descartar …?».
 */
function textosPrincipales(
  id: string,
  estado: Estado,
  porBloqueo: boolean,
  porSubtareas: boolean,
): { titulo: string; textoConfirmar: string } {
  if (estado === 'HECHO')
    return { titulo: `¿Cerrar ${id} como Hecha?`, textoConfirmar: 'Cerrar igual' };
  if (estado === 'DESCARTADA' && porSubtareas)
    return { titulo: `¿Cerrar ${id} como Descartada?`, textoConfirmar: 'Cerrar igual' };
  if (estado === 'DESCARTADA')
    return { titulo: `¿Descartar ${id}?`, textoConfirmar: 'Descartar igual' };
  if (porBloqueo && estado === 'EN_REVISION')
    return {
      titulo: `¿Pasar ${id} a En revisión aunque está bloqueada?`,
      textoConfirmar: 'Seguir igual',
    };
  return { titulo: `¿Empezar ${id} aunque está bloqueada?`, textoConfirmar: 'Empezar igual' };
}

/**
 * El diálogo. Lo pendiente sale de datos pedidos al abrirlo, no de la tarjeta ni de una caché vieja: las subtareas
 * de `?padre=N` (con `progresoSubtareas` del contrato) y los bloqueantes del detalle de la tarea.
 *
 * - Mientras carga no se dibuja (en la ficha ya suele estar fresco); si tarda, aparece con «Cargando…».
 * - Si ANTES de mostrarse resulta que no queda nada pendiente, sigue directo sin preguntar.
 * - Si ya se mostró, nunca se resuelve solo (la persona lo está leyendo): si los datos frescos dicen que ya no
 *   queda nada, el texto lo dice («Ya no hay nada pendiente») y la persona confirma o cancela.
 * - Si las subtareas fallan, lo dice y deja seguir; si falla el detalle, usa los bloqueantes que traía la tarea y,
 *   al cerrar, dice que no pudo revisar quiénes la esperan y deja seguir (sin detalle no se sabe).
 */
export function DialogoConfirmarMovimiento({
  tarea,
  estado,
  porSubtareas,
  porBloqueo,
  porQuienesEsperan,
  onConfirmar,
  onCancelar,
}: PropsDialogo) {
  // Las subtareas se piden aquí (la tarjeta del tablero solo trae el avance); en la ficha ya están en caché.
  const subtareas = useSubtareas(porSubtareas ? tarea.id : null);
  const conDetalle = porBloqueo || porQuienesEsperan;
  const detalle = useTareaDetalle(conDetalle ? tarea.id : null);
  const cargandoSubtareas = porSubtareas && sinDatosFrescos(subtareas);
  const cargandoDetalle = conDetalle && sinDatosFrescos(detalle);
  const cargandoBloqueo = porBloqueo && cargandoDetalle;
  const cargandoEsperan = porQuienesEsperan && cargandoDetalle;
  const cargando = cargandoSubtareas || cargandoDetalle;
  const mostrarCarga = useMostrarTrasRetardo(cargando);

  const hijasAbiertas = (subtareas.data ?? []).filter((hija) => !esEstadoCerrado(hija.estado));
  const conSubtareas =
    porSubtareas && (cargandoSubtareas || subtareas.isError || hijasAbiertas.length > 0);
  // Sin detalle (falló), sirven los bloqueantes que traía la tarea: es mejor que no avisar.
  const bloqueantes =
    detalle.isError || !detalle.data ? tarea.bloqueadaPor : detalle.data.bloqueadaPor;
  const esperando = porBloqueo ? bloqueantesAbiertos(bloqueantes) : [];
  const conBloqueo = porBloqueo && (cargandoBloqueo || esperando.length > 0);
  // Quiénes la esperan solo vienen en el detalle: si falló, no se sabe y se dice (en vez de cerrar sin preguntar).
  const errorEsperan = porQuienesEsperan && !cargandoEsperan && detalle.isError;
  const laEsperan =
    porQuienesEsperan && detalle.data && !detalle.isError
      ? detalle.data.bloqueaA.filter((t) => !esEstadoCerrado(t.estado))
      : [];
  const conEsperan = porQuienesEsperan && (cargandoEsperan || errorEsperan || laEsperan.length > 0);
  const sinPendientes = !cargando && !conSubtareas && !conBloqueo && !conEsperan;

  // ¿Ya se mostró alguna vez? Desde entonces, solo la persona lo cierra.
  const [visible, setVisible] = useState(false);
  const dibujar = visible || !(sinPendientes || (cargando && !mostrarCarga));
  // Ajuste durante el render (no en un efecto, como en `useMostrarTrasRetardo`): queda marcado antes de pintar.
  if (dibujar && !visible) setVisible(true);

  // Antes de mostrarse ya no queda nada pendiente: se sigue sin preguntar (una sola vez).
  const confirmarActual = useRef(onConfirmar);
  const yaSiguio = useRef(false);
  useEffect(() => {
    confirmarActual.current = onConfirmar;
  }, [onConfirmar]);
  useEffect(() => {
    if (!sinPendientes || visible || yaSiguio.current) return;
    yaSiguio.current = true;
    confirmarActual.current(false);
  }, [sinPendientes, visible]);

  if (!dibujar) return null;

  const id = formatearNumero(tarea.numero);
  const hecha = estado === 'HECHO';
  const como = hecha ? 'Hecha' : 'Descartada';
  const { titulo, textoConfirmar } = textosPrincipales(id, estado, porBloqueo, porSubtareas);
  const cantidad = subtareas.data ? hijasAbiertas.length : subtareasAbiertas(tarea);
  const progreso = (subtareas.data ? progresoSubtareas(subtareas.data) : null) ??
    tarea.progresoSubtareas ?? { total: 0, hechas: 0 };
  const avance = `Lleva ${progreso.hechas} de ${progreso.total} subtareas hechas`;
  const consecuenciaSubtareas = `Las subtareas abiertas no se cierran solas: seguirán en el tablero con su estado actual, como «Parte de» una tarea ${como.toLowerCase()}.`;

  let descripcion: string;
  let consecuencia: string | undefined;
  if (sinPendientes) {
    // Se mostró y los datos frescos dicen que ya no queda nada: lo dice, sin cerrarse solo.
    descripcion = 'Ya no hay nada pendiente: puedes seguir sin problema.';
    consecuencia = undefined;
  } else if (esEstadoCerrado(estado)) {
    // Cerrar: una madre con subtareas abiertas, una tarea bloqueada (como Hecha) y/o una que otras esperan.
    const otrasEsperan = conEsperan && !cargandoEsperan && !errorEsperan;
    descripcion = [
      conSubtareas && `${avance}.`,
      conBloqueo && !conSubtareas && 'Todavía hay tareas que la bloquean.',
      otrasEsperan && 'Otras tareas la están esperando.',
      conSubtareas && !hecha
        ? 'Revisa si sus subtareas también sobran.'
        : hecha
          ? 'Revisa que de verdad esté terminada.'
          : 'Revisa que de verdad no haga falta.',
    ]
      .filter(Boolean)
      .join(' ');
    const partes = [
      conSubtareas && consecuenciaSubtareas,
      otrasEsperan && consecuenciaQuienesEsperan(laEsperan, estado),
      conBloqueo && BLOQUEOS_EN_HISTORIAL,
    ].filter(Boolean);
    consecuencia = partes.length > 0 ? partes.join(' ') : undefined;
  } else {
    const cuantas =
      esperando.length > 1
        ? `${esperando.length} tareas que siguen abiertas`
        : 'otra que sigue abierta';
    descripcion = `Esta tarea espera a ${cuantas}. Si la ${
      estado === 'EN_CURSO' ? 'empiezas' : 'avanzas'
    } ahora, puede que tengas que rehacer trabajo.`;
    consecuencia = BLOQUEO_NO_SE_QUITA;
  }

  return (
    <DialogoConfirmar
      titulo={titulo}
      descripcion={descripcion}
      consecuencia={consecuencia}
      textoConfirmar={textoConfirmar}
      onConfirmar={() => onConfirmar(true)}
      onCancelar={onCancelar}
    >
      {conSubtareas && (
        <>
          <h3 className="confirmar__seccion">
            {cantidad === 1 ? 'Subtarea abierta' : `${cantidad} subtareas abiertas`}
          </h3>
          {cargandoSubtareas ? (
            <p className="dialogo__ayuda" role="status">
              Cargando subtareas…
            </p>
          ) : subtareas.isError ? (
            <p className="dialogo__error" role="alert">
              No pudimos cargar las subtareas abiertas. Revisa tu conexión; puedes cerrarla igual.
            </p>
          ) : (
            <ListaRelacionadas tareas={hijasAbiertas} etiqueta="Subtareas abiertas" />
          )}
        </>
      )}
      {conBloqueo && (
        <>
          <h3 className="confirmar__seccion">
            {hecha ? 'Todavía la bloquean' : 'Está esperando a'}
          </h3>
          {cargandoBloqueo ? (
            <p className="dialogo__ayuda" role="status">
              Cargando bloqueos…
            </p>
          ) : (
            <ListaRelacionadas tareas={esperando} etiqueta="Tareas que la bloquean" />
          )}
        </>
      )}
      {conEsperan && (
        <>
          <h3 className="confirmar__seccion">La esperan</h3>
          {cargandoEsperan ? (
            <p className="dialogo__ayuda" role="status">
              Cargando las tareas que la esperan…
            </p>
          ) : errorEsperan ? (
            <p className="dialogo__error" role="alert">
              No pudimos revisar si otras tareas la esperan. Revisa tu conexión; puedes{' '}
              {hecha ? 'cerrarla' : 'descartarla'} igual.
            </p>
          ) : (
            <ListaRelacionadas tareas={laEsperan} etiqueta="Tareas que la esperan" />
          )}
        </>
      )}
    </DialogoConfirmar>
  );
}

interface Pedido {
  tarea: TareaAMover;
  estado: Estado;
  porSubtareas: boolean;
  porBloqueo: boolean;
  porQuienesEsperan: boolean;
  seguir: (conDialogo: boolean) => void;
  alCancelar?: () => void;
}

/**
 * `confirmarCierre(tarea, estado, seguir, alCancelar?)`: si mover `tarea` a `estado` la cierra con subtareas
 * abiertas o con otras tareas abiertas esperándola, o la avanza estando bloqueada, muestra el diálogo (`dialogo`,
 * que quien llama dibuja; uno solo aunque pasen varias cosas) y llama a `seguir(true)` solo al confirmar; si no,
 * llama a `seguir(false)` sin preguntar (de inmediato o, al cerrar, cuando el detalle dice que nadie la espera).
 * `pidiendo` dice qué estado espera confirmación (para mostrarlo mientras tanto).
 */
export function useConfirmarCierre(): {
  confirmarCierre: (
    tarea: TareaAMover,
    estado: Estado,
    seguir: (conDialogo: boolean) => void,
    alCancelar?: () => void,
  ) => void;
  dialogo: ReactNode;
  pidiendo: Estado | null;
} {
  const [pedido, setPedido] = useState<Pedido | null>(null);

  const confirmarCierre = useCallback(
    (
      tarea: TareaAMover,
      estado: Estado,
      seguir: (conDialogo: boolean) => void,
      alCancelar?: () => void,
    ) => {
      const porSubtareas = pideConfirmarCierre(tarea, estado);
      const porBloqueo = pideConfirmarBloqueo(tarea, estado);
      const porQuienesEsperan = pideRevisarQuienesEsperan(tarea, estado);
      if (!porSubtareas && !porBloqueo && !porQuienesEsperan) {
        seguir(false);
        return;
      }
      setPedido({ tarea, estado, porSubtareas, porBloqueo, porQuienesEsperan, seguir, alCancelar });
    },
    [],
  );

  const dialogo = pedido ? (
    <DialogoConfirmarMovimiento
      tarea={pedido.tarea}
      estado={pedido.estado}
      porSubtareas={pedido.porSubtareas}
      porBloqueo={pedido.porBloqueo}
      porQuienesEsperan={pedido.porQuienesEsperan}
      onCancelar={() => {
        setPedido(null);
        pedido.alCancelar?.();
      }}
      onConfirmar={(conDialogo) => {
        setPedido(null);
        pedido.seguir(conDialogo);
      }}
    />
  ) : null;

  return { confirmarCierre, dialogo, pidiendo: pedido?.estado ?? null };
}
